import { useRef } from "react";

import { Skeleton } from "@mui/material";
import { Locales } from "@simplyk/common";
import { Control, FieldValues, Path, RegisterOptions, UseFormSetValue, useWatch } from "react-hook-form";

import { useTranslateField } from "./useTranslateField";

import { DashboardRichTextField } from "@/dashboard-design-system/RichTextField/DashboardRichTextField";

interface TranslatedRichTextFieldProps<T extends FieldValues> {
  fieldName: Path<T>;
  defaultFieldValue: string | undefined | null;
  defaultTranslationFieldValue: string | undefined | null;
  setValue: UseFormSetValue<T>;
  translationLocale: Locales;
  label: string;
  rules?: RegisterOptions<FieldValues, string>[] | undefined;
  control: Control<T> | undefined;
  errorMessage: string | undefined;
  dataTest: string;
  skipTranslation?: boolean;
}

export function TranslatedRichTextField<T extends FieldValues>({
  fieldName,
  defaultFieldValue,
  defaultTranslationFieldValue,
  setValue,
  translationLocale,
  label,
  rules,
  control,
  errorMessage,
  dataTest,
  skipTranslation = false,
}: TranslatedRichTextFieldProps<T>) {
  const { isTranslating } = useTranslateField({
    fieldName,
    defaultFieldValue,
    defaultTranslationFieldValue,
    setValue,
    translationLocale,
    skipTranslation,
  });

  const hasFrozenRef = useRef(false);
  const frozenDefaultValueRef = useRef<string | undefined>(undefined);
  const watchedValue = useWatch({ control, name: fieldName });

  if (!isTranslating && !hasFrozenRef.current) {
    hasFrozenRef.current = true;
    frozenDefaultValueRef.current = (watchedValue as string | undefined) ?? defaultTranslationFieldValue ?? undefined;
  }

  return isTranslating ? (
    <Skeleton variant="text" width="100%" height="100%" sx={{ marginTop: 1.5 }} />
  ) : (
    <DashboardRichTextField
      label={label}
      rules={rules}
      control={control}
      name={fieldName}
      defaultValue={frozenDefaultValueRef.current}
      errorMessage={errorMessage}
      data-test={dataTest}
    />
  );
}
